import type { ComponentType } from 'react';

import { GoogleDriveIcon } from '@atlassian/logo-third-party/google-drive';
import { MicrosoftOneDriveIcon } from '@atlassian/logo-third-party/microsoft-onedrive';
import { MicrosoftSharePointIcon } from '@atlassian/logo-third-party/microsoft-sharepoint';
import type { ThirdPartyLogoProps } from '@atlassian/logo-third-party/types';

export type SmartFolderRovoConnectorProvider = 'google-drive' | 'sharepoint' | 'onedrive';

export type SmartFolderProviderIcon = ComponentType<ThirdPartyLogoProps>;

export type SmartFolderRovoConnectorProviderConfig = {
	provider: SmartFolderRovoConnectorProvider;
	displayName: string;
	fullConnectorName: string;
	connectorNames: readonly string[];
	Icon: SmartFolderProviderIcon;
};

export const SMART_FOLDER_ROVO_CONNECTOR_PROVIDER_ORDER: readonly SmartFolderRovoConnectorProvider[] =
	['google-drive', 'sharepoint', 'onedrive'];

export const SMART_FOLDER_ROVO_CONNECTOR_PROVIDERS: {
	readonly 'google-drive': {
		readonly provider: 'google-drive';
		readonly displayName: 'Google Drive';
		readonly fullConnectorName: 'google-drive-connector';
		readonly connectorNames: readonly [
			'google-drive-connector',
			'smartlinks-connector-gdrive',
			'smartlinks-connector-gdrive-rsl',
			'lite-connector-gdrive',
		];
		readonly Icon: typeof GoogleDriveIcon;
	};
	readonly sharepoint: {
		readonly provider: 'sharepoint';
		readonly displayName: 'SharePoint';
		readonly fullConnectorName: 'sharepoint-connector';
		readonly connectorNames: readonly [
			'sharepoint-connector',
			'smartlinks-connector-sharepoint',
			'ai-3p-connector:sharepoint-domain',
		];
		readonly Icon: typeof MicrosoftSharePointIcon;
	};
	readonly onedrive: {
		readonly provider: 'onedrive';
		readonly displayName: 'OneDrive';
		readonly fullConnectorName: 'onedrive-connector';
		readonly connectorNames: readonly ['smartlinks-connector-onedrive', 'onedrive-connector'];
		readonly Icon: typeof MicrosoftOneDriveIcon;
	};
} = {
	'google-drive': {
		provider: 'google-drive',
		displayName: 'Google Drive',
		fullConnectorName: 'google-drive-connector',
		connectorNames: [
			'google-drive-connector',
			'smartlinks-connector-gdrive',
			'smartlinks-connector-gdrive-rsl',
			'lite-connector-gdrive',
		],
		Icon: GoogleDriveIcon,
	},
	sharepoint: {
		provider: 'sharepoint',
		displayName: 'SharePoint',
		fullConnectorName: 'sharepoint-connector',
		connectorNames: [
			'sharepoint-connector',
			'smartlinks-connector-sharepoint',
			'ai-3p-connector:sharepoint-domain',
		],
		Icon: MicrosoftSharePointIcon,
	},
	onedrive: {
		provider: 'onedrive',
		displayName: 'OneDrive',
		fullConnectorName: 'onedrive-connector',
		connectorNames: ['smartlinks-connector-onedrive', 'onedrive-connector'],
		Icon: MicrosoftOneDriveIcon,
	},
} as const satisfies Record<
	SmartFolderRovoConnectorProvider,
	SmartFolderRovoConnectorProviderConfig
>;
